import {
  ArrowPathIcon,
  BellAlertIcon,
  BellSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import AdminApiService from '../../utils/api';
import PhoneActions from '../../components/PhoneActions';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'buyer', label: 'Buyers' },
  { value: 'seller', label: 'Sellers' },
  { value: 'agent', label: 'Agents' },
  { value: 'admin', label: 'Admins' },
];

const APP_OPTIONS = [
  { value: 'all', label: 'All Apps' },
  { value: 'buyer-app', label: 'Buyer App' },
  { value: 'seller-app', label: 'Seller App' },
  { value: 'agent-dashboard', label: 'Agent Dashboard' },
];

const TARGET_OPTIONS = [
  { value: 'all', label: 'Everyone with notifications enabled' },
  { value: 'role', label: 'A specific role' },
  { value: 'app', label: 'Users of a specific app' },
  { value: 'users', label: 'Selected users only' },
];

const RoleBadge = ({ role }) => {
  const styles = {
    buyer: 'bg-blue-50 text-blue-700 border-blue-200',
    seller: 'bg-orange-50 text-orange-700 border-orange-200',
    agent: 'bg-purple-50 text-purple-700 border-purple-200',
    admin: 'bg-gray-100 text-gray-800 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
        styles[role] || styles.buyer
      }`}
    >
      {role || 'buyer'}
    </span>
  );
};

const PushStatus = ({ user }) => {
  if (user.pushEnabled) {
    const parts = [];
    if (user.fcmTokenCount > 0) parts.push(user.fcmTokenCount + ' FCM');
    if (user.expoTokenCount > 0) parts.push(user.expoTokenCount + ' Expo');
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700 bg-success-50 border border-success-200 px-2 py-0.5 rounded-full">
        <BellAlertIcon className="w-3 h-3" />
        Push enabled · {parts.join(' + ')}
      </span>
    );
  }
  if (!user.pushPreferenceEnabled) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
        <BellSlashIcon className="w-3 h-3" />
        Muted by user
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-warning-700 bg-warning-50 border border-warning-200 px-2 py-0.5 rounded-full">
      <ExclamationTriangleIcon className="w-3 h-3" />
      No registered device
    </span>
  );
};

const NotificationsBroadcast = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    pushEnabledUsers: 0,
    pushDisabledUsers: 0,
    totalActiveTokens: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [appFilter, setAppFilter] = useState('all');
  const [pushOnlyFilter, setPushOnlyFilter] = useState(false);

  const [target, setTarget] = useState('all');
  const [targetRole, setTargetRole] = useState('buyer');
  const [targetApp, setTargetApp] = useState('buyer-app');
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('promotional');
  const [pushOnlyDelivery, setPushOnlyDelivery] = useState(true);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);

  const fetchUsers = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (appFilter !== 'all') params.app = appFilter;
      if (pushOnlyFilter) params.pushOnly = 'true';
      if (search.trim()) params.search = search.trim();

      const response = await AdminApiService.getNotificationUsers(params);
      setUsers(Array.isArray(response?.data) ? response.data : []);
      setStats(response?.stats || stats);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load users');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await AdminApiService.listBroadcasts({ limit: 10 });
      setHistory(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      // History is non-critical, fail silently
      console.error('Failed to load broadcast history', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change (debounced for search)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, appFilter, pushOnlyFilter]);

  const filteredUsers = useMemo(() => users, [users]);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAllVisiblePushEnabled = () => {
    const ids = filteredUsers.filter((u) => u.pushEnabled).map((u) => u._id);
    setSelectedUserIds(new Set(ids));
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  const estimatedRecipients = useMemo(() => {
    if (target === 'users') {
      return selectedUserIds.size;
    }
    let pool = users;
    if (target === 'role') {
      pool = pool.filter((u) => u.role === targetRole);
    } else if (target === 'app') {
      pool = pool.filter((u) => (u.apps || []).includes(targetApp));
    }
    if (pushOnlyDelivery) {
      pool = pool.filter((u) => u.pushEnabled);
    }
    return pool.length;
  }, [target, targetRole, targetApp, users, selectedUserIds, pushOnlyDelivery]);

  const handleSend = async (event) => {
    event.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }

    if (target === 'users' && selectedUserIds.size === 0) {
      toast.error('Pick at least one user from the list below');
      return;
    }

    const confirmText =
      target === 'users'
        ? `Send to ${selectedUserIds.size} selected user(s)?`
        : `Send to approximately ${estimatedRecipients} user(s)?`;

    if (!window.confirm(confirmText)) return;

    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        type,
        target,
        pushOnly: pushOnlyDelivery,
      };
      if (target === 'role') payload.role = targetRole;
      if (target === 'app') payload.app = targetApp;
      if (target === 'users') payload.userIds = Array.from(selectedUserIds);

      const response = await AdminApiService.sendBroadcastNotification(payload);
      const recipients = response?.data?.recipients ?? 0;
      toast.success(`Broadcast queued for ${recipients} recipient(s)`);
      setTitle('');
      setMessage('');
      clearSelection();
      fetchHistory();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
        <h1 className="text-3xl font-display font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-600 mt-2">
          Send push notifications to users across the buyer and seller apps. Users without any
          registered device token (FCM or Expo) are clearly marked.
        </p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-soft border border-gray-200">
          <p className="text-sm text-gray-600">Total users</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalUsers}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-soft border border-gray-200">
          <p className="text-sm text-gray-600">Push enabled</p>
          <p className="text-2xl font-bold text-success-600 mt-1">{stats.pushEnabledUsers}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-soft border border-gray-200">
          <p className="text-sm text-gray-600">No permission</p>
          <p className="text-2xl font-bold text-warning-600 mt-1">{stats.pushDisabledUsers}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-soft border border-gray-200">
          <p className="text-sm text-gray-600">Active devices</p>
          <p className="text-2xl font-bold text-primary-600 mt-1">{stats.totalActiveTokens}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <motion.form
          onSubmit={handleSend}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-1 bg-white rounded-xl p-6 shadow-soft border border-gray-200 space-y-4 h-fit"
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <PaperAirplaneIcon className="w-5 h-5 text-primary-500" />
            Compose Broadcast
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              required
              placeholder="e.g. New cars listed in your area"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">{title.length}/100</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={500}
              required
              rows={4}
              placeholder="Short, friendly notification body..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">{message.length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="promotional">Promotional / Marketing</option>
              <option value="system_update">System update</option>
              <option value="maintenance">Maintenance</option>
              <option value="welcome">Welcome / Onboarding</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {TARGET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {target === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={targetRole}
                onChange={(e) => setTargetRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {ROLE_OPTIONS.filter((r) => r.value !== 'all').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {target === 'app' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">App</label>
              <select
                value={targetApp}
                onChange={(e) => setTargetApp(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {APP_OPTIONS.filter((a) => a.value !== 'all').map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {target === 'users' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-700">
                <span className="font-semibold">{selectedUserIds.size}</span> user(s) selected from
                the list →
              </p>
              {selectedUserIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-xs text-primary-600 hover:underline mt-1"
                >
                  Clear selection
                </button>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={pushOnlyDelivery}
              onChange={(e) => setPushOnlyDelivery(e.target.checked)}
              className="rounded text-primary-600 focus:ring-primary-500"
            />
            Only deliver to devices with push enabled
          </label>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-700">
            <p>
              Estimated recipients:{' '}
              <span className="font-semibold">{estimatedRecipients}</span>
            </p>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {sending ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-4 h-4" /> Send broadcast
              </>
            )}
          </button>
        </motion.form>

        {/* User list + history */}
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-soft border border-gray-200"
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <UserGroupIcon className="w-5 h-5 text-primary-500" />
                  Recipients
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search name, email, phone..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={appFilter}
                    onChange={(e) => setAppFilter(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {APP_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={pushOnlyFilter}
                      onChange={(e) => setPushOnlyFilter(e.target.checked)}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    Push-enabled only
                  </label>
                </div>
              </div>

              {target === 'users' && (
                <div className="flex items-center justify-between mt-3 text-sm">
                  <button
                    type="button"
                    onClick={selectAllVisiblePushEnabled}
                    className="text-primary-600 hover:underline"
                  >
                    Select all push-enabled in current view
                  </button>
                  <span className="text-gray-500">
                    {selectedUserIds.size} selected · {filteredUsers.length} shown
                  </span>
                </div>
              )}
            </div>

            <div className="max-h-[640px] overflow-y-auto divide-y divide-gray-100">
              {refreshing && filteredUsers.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-500">Loading users...</div>
              )}
              {!refreshing && filteredUsers.length === 0 && (
                <div className="p-8 text-center text-sm text-gray-500">
                  No users match the current filters.
                </div>
              )}
              {filteredUsers.map((user) => {
                const selected = selectedUserIds.has(user._id);
                return (
                  <div
                    key={user._id}
                    className={`p-4 flex items-center gap-4 hover:bg-gray-50 ${
                      target === 'users' && selected ? 'bg-primary-50' : ''
                    }`}
                  >
                    {target === 'users' && (
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleUserSelection(user._id)}
                        disabled={!user.pushEnabled && pushOnlyDelivery}
                        className="rounded text-primary-600 focus:ring-primary-500"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 truncate">{user.name}</p>
                        <RoleBadge role={user.role} />
                        {user.sellerType === 'agent-created' && (
                          <span className="text-[10px] uppercase tracking-wide text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">
                            Agent-created
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap mt-0.5">
                        <span className="truncate">{user.email || '—'}</span>
                        <PhoneActions phone={user.phone} empty="—" textClassName="text-xs text-gray-500 font-normal" />
                      </div>
                      {user.apps?.length > 0 && (
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Apps: {user.apps.join(', ')}
                        </p>
                      )}
                    </div>
                    <PushStatus user={user} />
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* History */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-soft border border-gray-200"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent broadcasts</h2>
              <button
                type="button"
                onClick={fetchHistory}
                className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1"
              >
                <ArrowPathIcon className="w-4 h-4" /> Refresh
              </button>
            </div>
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {history.length === 0 && (
                <p className="p-6 text-sm text-gray-500 text-center">No broadcasts yet.</p>
              )}
              {history.map((item) => (
                <div key={item._id} className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-gray-900 truncate">{item.title}</p>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(item.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.message}</p>
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <UserGroupIcon className="w-3 h-3" /> {item.recipients} recipient(s)
                    </span>
                    <span className="inline-flex items-center gap-1 text-success-600">
                      <CheckCircleIcon className="w-3 h-3" /> {item.delivered} delivered
                    </span>
                    <span className="capitalize text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                      {String(item.type || '').replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsBroadcast;
